import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.kovaranalytics.com';
const SITE_NAME = 'Kovar Analytics';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * Per-route head manager.
 *
 * Props:
 *   title          — full <title>; we append the brand suffix unless `noBrand`
 *   description    — meta description (≈ 150-160 chars)
 *   path           — canonical URL path (e.g. `/services` or `/pricelist/bpc-157-analysis`)
 *   image          — OG image override (absolute URL)
 *   type           — og:type (default `website`)
 *   keywords       — optional comma-separated keywords (used sparingly)
 *   jsonLd         — single JSON-LD object or array of objects (Schema.org structured data)
 *   noBrand        — skip the " · Kovar Analytics" suffix on <title>
 *   noindex        — emit robots `noindex,follow` (for cart/payment/account)
 */
function SEO({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  keywords,
  jsonLd,
  noBrand = false,
  noindex = false,
}) {
  const fullTitle = noBrand ? title : `${title} · ${SITE_NAME}`;
  const canonical = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="robots" content={noindex ? 'noindex,follow' : 'index, follow'} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {ldArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

/* ----------------------- structured data helpers ----------------------- */

export const orgJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/kovar_logo.png`,
  email: 'info@kovaranalytics.com',
  sameAs: [],
  description:
    'Independent analytical laboratory specialising in peptide purity testing, anabolic steroid analysis, SARMs and pharmaceutical compound testing. HPLC-based identification, quantification and purity with worldwide shipping.',
});

export const websiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/services?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: it.url.startsWith('http') ? it.url : `${SITE_URL}${it.url}`,
  })),
});

export const productJsonLd = ({ name, description, image, slug, price, category }) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  image: image
    ? image.startsWith('http')
      ? image
      : `${SITE_URL}${image}`
    : DEFAULT_OG_IMAGE,
  sku: slug,
  brand: { '@type': 'Brand', name: SITE_NAME },
  category,
  offers: {
    '@type': 'Offer',
    url: `${SITE_URL}/pricelist/${slug}`,
    priceCurrency: 'USD',
    price: String(price),
    availability: 'https://schema.org/InStock',
    seller: { '@type': 'Organization', name: SITE_NAME },
  },
});

export const faqJsonLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

export { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE };
export default SEO;
